import * as React from 'react'
import Select from '../../../shared/base_react_select'
import I18n from '../../../../libs/i18n'
import { Column, FilterOption, FilterValue, StatusOption } from '../types'
import { getFilterType } from './filter_utils'

interface FilterControlProps {
  column: Column
  value?: FilterValue
  options?: FilterOption[]
  onChange: (columnKey: string, value?: FilterValue) => void
}

const toggleCheckboxValue = <T extends string | number>(currentValues: T[], value: T): T[] =>
  currentValues.includes(value)
    ? currentValues.filter((currentValue) => currentValue !== value)
    : [...currentValues, value]

export default function FilterControl({
  column,
  value,
  options = [],
  onChange,
}: FilterControlProps) {
  const filterType = getFilterType(column)

  if (filterType === 'select') {
    const selectedValues = Array.isArray(value) ? value as string[] : []
    const statusOptions = (column.column_options || []) as unknown as StatusOption[]

    return (
      <div className="d-flex flex-wrap">
        {statusOptions.map((option) => (
          <label key={option.value} className="d-flex align-items-center mr-3 mb-2" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => {
                const nextValue = toggleCheckboxValue(selectedValues, option.value)
                onChange(column.key, nextValue.length > 0 ? nextValue : undefined)
              }}
              style={{ marginRight: '4px' }}
            />
            {option.label ?? option.value}
          </label>
        ))}
      </div>
    )
  }

  if (filterType === 'select_id') {
    const selectOptions = (column.column_options || [])
      .filter((option) => typeof option.id === 'number')
      .map((option) => ({ value: option.id as number, label: option.label }))
    const selectedValues = Array.isArray(value) ? value as number[] : []

    if (selectOptions.length <= 8) {
      return (
        <div className="d-flex flex-wrap">
          {selectOptions.map((option) => (
            <label key={option.value} className="d-flex align-items-center mr-3 mb-2" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => {
                  const nextValue = toggleCheckboxValue(selectedValues, option.value)
                  onChange(column.key, nextValue.length > 0 ? nextValue : undefined)
                }}
                style={{ marginRight: '4px' }}
              />
              {option.label}
            </label>
          ))}
        </div>
      )
    }

    return (
      <Select
        isMulti
        value={selectOptions.filter((option) => selectedValues.includes(option.value))}
        options={selectOptions}
        onChange={(selectedOptions: FilterOption[] | null) => {
          const nextValue = (selectedOptions || []).map((option) => option.value)
          onChange(column.key, nextValue.length > 0 ? nextValue : undefined)
        }}
        placeholder={I18n.t('project_groups.overview.saved_filters.modal.select_placeholder')}
      />
    )
  }

  if (filterType === 'association_single' || filterType === 'association_multi') {
    const selectedValues = Array.isArray(value) ? value as number[] : []

    return (
      <Select
        isMulti
        value={options.filter((option) => selectedValues.includes(option.value))}
        options={options}
        onChange={(selectedOptions: FilterOption[] | null) => {
          const nextValue = (selectedOptions || []).map((option) => option.value)
          onChange(column.key, nextValue.length > 0 ? nextValue : undefined)
        }}
        placeholder={I18n.t('project_groups.overview.saved_filters.modal.select_placeholder')}
      />
    )
  }

  if (filterType === 'text') {
    return (
      <input
        type="text"
        className="form-control"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(column.key, event.target.value.trim() ? event.target.value : undefined)}
        placeholder={I18n.t('project_groups.overview.saved_filters.modal.text_filter_placeholder')}
      />
    )
  }

  if (filterType === 'number') {
    const bounds = !Array.isArray(value) && typeof value === 'object' ? value as { min?: number; max?: number } : {}

    return (
      <div className="row">
        <div className="col-6">
          <input
            type="number"
            className="form-control"
            value={bounds.min ?? ''}
            onChange={(event) => {
              const nextValue = event.target.value === '' ? undefined : Number(event.target.value)
              const filterValue = { ...bounds, min: nextValue }
              onChange(
                column.key,
                filterValue.min == null && filterValue.max == null ? undefined : filterValue
              )
            }}
            placeholder={I18n.t('project_groups.overview.saved_filters.modal.number_min_placeholder')}
          />
        </div>
        <div className="col-6">
          <input
            type="number"
            className="form-control"
            value={bounds.max ?? ''}
            onChange={(event) => {
              const nextValue = event.target.value === '' ? undefined : Number(event.target.value)
              const filterValue = { ...bounds, max: nextValue }
              onChange(
                column.key,
                filterValue.min == null && filterValue.max == null ? undefined : filterValue
              )
            }}
            placeholder={I18n.t('project_groups.overview.saved_filters.modal.number_max_placeholder')}
          />
        </div>
      </div>
    )
  }

  const bounds = !Array.isArray(value) && typeof value === 'object' ? value as { from?: string; to?: string } : {}

  return (
    <div className="row">
      <div className="col-6">
        <input
          type="date"
          className="form-control"
          value={bounds.from || ''}
          onChange={(event) => {
            const filterValue = { ...bounds, from: event.target.value || undefined }
            onChange(column.key, !filterValue.from && !filterValue.to ? undefined : filterValue)
          }}
          placeholder={I18n.t('project_groups.overview.saved_filters.modal.date_from_placeholder')}
        />
      </div>
      <div className="col-6">
        <input
          type="date"
          className="form-control"
          value={bounds.to || ''}
          onChange={(event) => {
            const filterValue = { ...bounds, to: event.target.value || undefined }
            onChange(column.key, !filterValue.from && !filterValue.to ? undefined : filterValue)
          }}
          placeholder={I18n.t('project_groups.overview.saved_filters.modal.date_to_placeholder')}
        />
      </div>
    </div>
  )
}
